import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {InputText} from '@/components/commons/input-text/InputText'
import {Button} from '@/components/ui/button/Button'
import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {PlusIcon, Trash01Icon} from '@/components/ui/icon/Icon'
import {InputSelectAccessType} from '@/features/access-types/components/input-select-access-type/InputSelectAccessType'
import {AccessType} from '@/features/access-types/types'
import {PlannedAccessesFrequencyCounter} from '@/features/planned-accesses/components/accesses-form/planned-accesses-frequency-counter/PlannedAccessesFrequencyCounter'
import {PlannedAccessesFieldsSchema} from '@/features/planned-accesses/formSchemas'
import {accessFrequencyMapToLabel} from '@/features/planned-accesses/utils'
import {projectSectorToLabel} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'
import {ObjectEntries} from '@/utilities/helpers'
import {Controller, useFieldArray, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const PlannedAccessesTreatmentFieldsSchema = PlannedAccessesFieldsSchema.pick({
    inPersonAccesses: true,
    projectId: true,
    isMixedProject: true
})
type PlannedAccessesTreatmentFieldsSchema = z.infer<typeof PlannedAccessesTreatmentFieldsSchema>

export const PlannedAccessesTreatmentFields = ({
    isLoadingProject,
    projectDurationDays
}: {
    isLoadingProject: boolean
    projectDurationDays?: number | null
}) => {
    const {t} = useTranslation()
    const {control, formState} = useFormContext<PlannedAccessesTreatmentFieldsSchema>()
    const isMixedProject = useWatch({control, name: 'isMixedProject'})
    const fields = useWatch({control, name: 'inPersonAccesses'})
    const {
        fields: fieldArray,
        append,
        remove
    } = useFieldArray({
        control,
        name: 'inPersonAccesses',
        keyName: 'fieldId'
    })

    const onAddTreatment = () => {
        append({
            accessType: undefined,
            frequency: 'weekly',
            numberOfAccesses: undefined,
            sector: undefined
        })
    }

    const getDisabledSectorsForField = (currentIndex: number): string[] => {
        if (!isMixedProject) {
            // For non-mixed projects, disable all already selected sectors
            return (
                fields
                    ?.map(field => field.sector)
                    .filter(
                        (sector): sector is 'outpatient' | 'domiciliary' =>
                            sector === 'outpatient' || sector === 'domiciliary'
                    ) ?? []
            )
        }

        // For mixed projects, consider only sectors with the same accessType
        const currentField = fields?.[currentIndex]
        const currentAccessType = currentField?.accessType?.accessType

        if (!currentAccessType) {
            // If accessType is not yet selected, don't disable anything
            return []
        }

        // Find all sectors already selected with the same accessType (excluding current field)
        return (
            fields
                ?.filter(
                    (field, index) =>
                        index !== currentIndex && field.sector && field.accessType?.accessType === currentAccessType
                )
                .map(field => field.sector!)
                .filter(
                    (sector): sector is 'outpatient' | 'domiciliary' =>
                        sector === 'outpatient' || sector === 'domiciliary'
                ) ?? []
        )
    }

    const getDisabledAccessTypesForField = (currentIndex: number): AccessType['accessType'][] => {
        if (!isMixedProject) {
            // For non-mixed projects, disable all already selected accessTypes
            return (
                fields
                    ?.map(field => field.accessType?.accessType)
                    .filter((accessType): accessType is AccessType['accessType'] => accessType !== undefined) ?? []
            )
        }

        // For mixed projects, consider only accessTypes with the same sector
        const currentField = fields?.[currentIndex]
        const currentSector = currentField?.sector

        if (!currentSector) {
            // If sector is not yet selected, don't disable anything
            return []
        }

        // Find all accessTypes already selected with the same sector (excluding current field)
        return (
            fields
                ?.filter(
                    (field, index) =>
                        index !== currentIndex && field.accessType?.accessType && field.sector === currentSector
                )
                .map(field => field.accessType!.accessType) ?? []
        )
    }

    return (
        <>
            {fieldArray.map((field, index) => (
                <Flexbox key={field.fieldId} fullWidth gap={2} align="flex-end">
                    <Grid columns={2} fullWidth gap={2.5}>
                        <GridItem gap={2}>
                            {isMixedProject && (
                                <Controller
                                    control={control}
                                    name={`inPersonAccesses.${index}.sector`}
                                    render={({field}) => (
                                        <InputSelect
                                            label={t('accesses:mode')}
                                            placeholder={t('commons:select')}
                                            onChange={field.onChange}
                                            value={field.value ?? ''}
                                            errorMessage={formState.errors?.inPersonAccesses?.[index]?.sector?.message}
                                            disabledOptions={getDisabledSectorsForField(index)}
                                            optionsEntries={ObjectEntries(projectSectorToLabel)
                                                .filter(
                                                    ([key]) =>
                                                        key != 'mixed' && key != 'maintenance' && key != 'extended'
                                                )
                                                .map(([key, value]) => [key, t(value)])}
                                        />
                                    )}
                                />
                            )}
                            <Controller
                                control={control}
                                name={`inPersonAccesses.${index}.accessType`}
                                render={({field}) => (
                                    <InputSelectAccessType
                                        label={t('commons:access_type')}
                                        placeholder={t('commons:select')}
                                        onChange={field.onChange}
                                        value={field.value}
                                        groupName="treatments"
                                        errorMessage={formState.errors?.inPersonAccesses?.[index]?.accessType?.message}
                                        disabledAccessTypes={getDisabledAccessTypesForField(index)}
                                    />
                                )}
                            />
                        </GridItem>
                        <GridItem justify="space-between" gap={2}>
                            <Controller
                                control={control}
                                name={`inPersonAccesses.${index}.numberOfAccesses`}
                                render={({field}) => (
                                    <InputText
                                        label={t('commons:number')}
                                        type="number"
                                        value={field.value ?? ''}
                                        placeholder="-"
                                        errorMessage={
                                            formState.errors?.inPersonAccesses?.[index]?.numberOfAccesses?.message
                                        }
                                        onChange={event =>
                                            field.onChange(event.target.value ? parseInt(event.target.value) : '')
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={`inPersonAccesses.${index}.frequency`}
                                render={({field}) => (
                                    <InputSelect
                                        label={t('planned_accesses:frequency')}
                                        placeholder={t('commons:select')}
                                        optionsMap={accessFrequencyMapToLabel}
                                        onChange={field.onChange}
                                        value={field.value}
                                        errorMessage={formState.errors?.inPersonAccesses?.[index]?.frequency?.message}
                                    />
                                )}
                            />

                            <PlannedAccessesFrequencyCounter
                                isLoading={isLoadingProject}
                                duration={projectDurationDays ?? 0}
                                index={index}
                            />
                        </GridItem>
                    </Grid>
                    <Button
                        variant="ghost"
                        onClick={() => remove(index)}
                        shape="square"
                        disabled={fieldArray.length == 1}
                    >
                        <Trash01Icon />
                    </Button>
                </Flexbox>
            ))}
            <Divider direction="horizontal" />
            <Button variant="secondary" size="sm" onClick={onAddTreatment}>
                <PlusIcon />
                {t('commons:treatment')}
            </Button>
        </>
    )
}
